import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, ArrowRight, Send } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm, type Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { submitApplication } from '../api'
import type { PublicAdmissions, SubmitResult } from '../types'
import {
  applicationSchema,
  buildPayload,
  clearDraft,
  emptyApplication,
  loadDraft,
  missingDocuments,
  saveDraft,
  STEP_FIELDS,
  STEP_LABELS,
  stepOfField,
  stepsFor,
  type ApplicationForm,
  type StepKey,
  type UploadedDocs,
} from '../wizard'
import { AdmissionConfirmation } from './AdmissionOutcome'
import { StepProgress } from './StepProgress'
import { ClassStep, DocumentsStep, GuardiansStep, ReviewStep, StudentStep } from './WizardSteps'

const STEP_INTRO: Record<StepKey, string> = {
  class: 'Which class is the application for?',
  student: 'About the student',
  guardians: 'Parents and guardians',
  documents: 'Previous school and documents',
  review: 'Check and send',
}

function without<T>(record: Record<number, T>, index: number): Record<number, T> {
  return Object.fromEntries(Object.entries(record).filter(([k]) => Number(k) !== index))
}

/**
 * The public application, step by step. Each step is checked before moving on; a draft is kept in
 * this browser (with the documents already sent) so a refresh loses nothing.
 */
export function AdmissionWizard({ info, onClosed }: { info: PublicAdmissions; onClosed?: () => void }) {
  const { t } = useTranslation('admissions')
  const steps = useMemo(() => stepsFor(info), [info])
  const yearId = info.academicYear?.id ?? null
  const [draft] = useState(() => loadDraft(yearId))
  const schema = useMemo(() => applicationSchema(info.guardiansEnabled), [info.guardiansEnabled])
  const form = useForm<ApplicationForm>({
    resolver: zodResolver(schema),
    defaultValues: draft?.values ?? emptyApplication(info.guardiansEnabled),
    mode: 'onTouched',
  })
  const initialStep = draft ? Math.max(0, steps.indexOf(draft.step)) : 0
  const [step, setStep] = useState(initialStep)
  const [reached, setReached] = useState(initialStep)
  const [docs, setDocs] = useState<UploadedDocs>(draft?.docs ?? {})
  const [docErrors, setDocErrors] = useState<Record<number, string>>({})
  const [restored, setRestored] = useState(draft !== null)
  const [result, setResult] = useState<SubmitResult | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const honeypot = useRef<HTMLInputElement>(null)
  const heading = useRef<HTMLHeadingElement>(null)
  const mounted = useRef(false)
  const key = steps[step] ?? 'class'

  // Keep the draft up to date (values, documents, step).
  useEffect(() => {
    if (result) return
    saveDraft({ step: key, values: form.getValues(), docs, yearId })
    const sub = form.watch((values) =>
      saveDraft({ step: key, values: values as ApplicationForm, docs, yearId }),
    )
    return () => sub.unsubscribe()
  }, [form, key, docs, yearId, result])

  // Move the focus to the new step's title (screen readers announce it).
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    const target = result ? document.getElementById('step-heading') : heading.current
    target?.focus()
    target?.scrollIntoView({ block: 'center' })
  }, [step, result])

  const checkStep = async (k: StepKey): Promise<boolean> => {
    const fields = STEP_FIELDS[k] as Path<ApplicationForm>[]
    const ok = fields.length ? await form.trigger(fields, { shouldFocus: true }) : true
    if (k !== 'documents') return ok
    const missing = missingDocuments(info.requiredDocuments, docs)
    setDocErrors(Object.fromEntries(missing.map((d) => [d.index, 'This document is required.'])))
    return ok && missing.length === 0
  }

  const goTo = (i: number) => {
    setFormError(null)
    setStep(i)
    setReached((r) => Math.max(r, i))
  }
  const next = async () => {
    if (await checkStep(key)) goTo(step + 1)
  }

  const send = async () => {
    setFormError(null)
    if (!form.getValues('consent')) {
      form.setError('consent', { type: 'manual', message: 'Please confirm that the information is correct.' })
      return
    }
    for (const [i, k] of steps.entries()) {
      if (!(await checkStep(k))) {
        setStep(i)
        return
      }
    }
    setSending(true)
    const payload = buildPayload(form.getValues(), docs, honeypot.current?.value ?? '')
    try {
      const res = await submitApplication(payload)
      clearDraft()
      setResult(res)
    } catch (e) {
      const err = toApiError(e)
      if (err.status === 422 && err.violations.length) {
        let first = steps.length - 1
        let nextDocs = { ...docs }
        const nextDocErrors: Record<number, string> = {}
        for (const v of err.violations) {
          const docIndex = /^requiredDocuments\[(\d+)\]/.exec(v.field)?.[1]
          const sent = /^documents\[(\d+)\]/.exec(v.field)?.[1]
          if (docIndex !== undefined) nextDocErrors[Number(docIndex)] = v.message
          else if (sent !== undefined) {
            const index = payload.documents[Number(sent)]?.index
            if (index !== undefined) {
              nextDocs = without(nextDocs, index)
              nextDocErrors[index] = v.message
            }
          } else {
            form.setError(v.field.replace(/\[(\d+)\]/g, '.$1') as Path<ApplicationForm>, {
              type: 'server',
              message: v.message,
            })
          }
          first = Math.min(first, Math.max(0, steps.indexOf(stepOfField(v.field))))
        }
        setDocs(nextDocs)
        setDocErrors(nextDocErrors)
        setStep(first)
        setFormError(err.message)
      } else {
        setFormError(err.message)
        if (err.status === 409) onClosed?.()
      }
    } finally {
      setSending(false)
    }
  }

  if (result) return <AdmissionConfirmation info={info} result={result} />

  return (
    <FormProvider {...form}>
      <Card className="p-4 sm:p-6">
        <StepProgress steps={steps} current={step} reached={reached} onGo={goTo} />

        {restored ? (
          <Alert
            tone="info"
            className="mt-4"
            action={
              <Button
                variant="ghost"
                size="sm"
                className="h-auto whitespace-normal"
                onClick={() => {
                  clearDraft()
                  form.reset(emptyApplication(info.guardiansEnabled))
                  setDocs({})
                  setDocErrors({})
                  setRestored(false)
                  setStep(0)
                  setReached(0)
                }}
              >
                {t('Start over')}
              </Button>
            }
          >
            {t('We restored the application you started on this device.')}
          </Alert>
        ) : null}

        <form
          noValidate
          className="mt-5"
          onSubmit={(e) => {
            e.preventDefault()
            void (key === 'review' ? send() : next())
          }}
        >
          <h2 ref={heading} tabIndex={-1} className="mb-1 text-lg font-semibold text-ink focus:outline-none">
            {t(STEP_INTRO[key])}
          </h2>
          <p className="mb-4 text-xs text-muted">
            {t('Step {{n}} of {{total}}', { n: step + 1, total: steps.length })} · {t(STEP_LABELS[key])}
          </p>

          {formError ? (
            <Alert tone="danger" className="mb-4">
              {formError}
            </Alert>
          ) : null}

          {/* Hidden from people; robots fill it in and their application is dropped. */}
          <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
            <label htmlFor="adm-website">{t('Website')}</label>
            <input
              ref={honeypot}
              id="adm-website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {key === 'class' ? <ClassStep info={info} /> : null}
          {key === 'student' ? <StudentStep info={info} /> : null}
          {key === 'guardians' ? <GuardiansStep /> : null}
          {key === 'documents' ? (
            <DocumentsStep
              info={info}
              docs={docs}
              docErrors={docErrors}
              onDocChange={(index, value) => {
                setDocs((d) => (value ? { ...d, [index]: value } : without(d, index)))
                setDocErrors((e) => without(e, index))
              }}
            />
          ) : null}
          {key === 'review' ? (
            <ReviewStep info={info} docs={docs} onEdit={(k) => goTo(Math.max(0, steps.indexOf(k)))} />
          ) : null}

          <div className="mt-6 grid grid-cols-1 gap-2 sm:flex sm:flex-row-reverse sm:justify-between">
            {key === 'review' ? (
              <Button
                type="submit"
                variant="gradient"
                size="lg"
                loading={sending}
                rightIcon={<Send />}
                className="h-auto min-h-11 py-2.5 text-center leading-snug whitespace-normal"
              >
                {t('Send the application')}
              </Button>
            ) : (
              <Button
                type="submit"
                size="lg"
                rightIcon={<ArrowRight />}
                className="h-auto min-h-11 py-2.5 text-center leading-snug whitespace-normal"
              >
                {t('Next')}
              </Button>
            )}
            {step > 0 ? (
              <Button
                variant="secondary"
                size="lg"
                leftIcon={<ArrowLeft />}
                onClick={() => goTo(step - 1)}
                className="h-auto min-h-11 py-2.5 text-center leading-snug whitespace-normal"
              >
                {t('Back')}
              </Button>
            ) : null}
          </div>
        </form>
      </Card>
    </FormProvider>
  )
}
